import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowDownLeft,
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  Scale,
  Trash2,
  UserPlus,
  UsersRound,
} from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import TeamCreditEntryDialog from "../components/TeamCreditEntryDialog";
import api, { apiErrorText } from "../lib/api";
import { currentNepalMonth, monthAdd, monthLabel } from "../lib/partyBook";
import { fmtTeamCredit, TEAM_CREDIT_CURRENCIES } from "../lib/teamCredit";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const EMPTY_MEMBER = { name: "", contact: "", notes: "" };

export default function TeamCreditDetails() {
  const navigate = useNavigate();
  const { teamId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState("all");
  const [month, setMonth] = useState(currentNepalMonth());
  const requestedCurrency = searchParams.get("currency");
  const [currency, setCurrency] = useState(TEAM_CREDIT_CURRENCIES.includes(requestedCurrency) ? requestedCurrency : "NPR");
  const [memberFilter, setMemberFilter] = useState("all");
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberRows, setMemberRows] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [memberOpen, setMemberOpen] = useState(false);
  const [memberForm, setMemberForm] = useState(EMPTY_MEMBER);
  const [editingMember, setEditingMember] = useState(null);
  const [deletingMember, setDeletingMember] = useState(null);
  const [savingMember, setSavingMember] = useState(false);
  const [deletingMemberBusy, setDeletingMemberBusy] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const filterParams = useMemo(() => {
    const params = { period, currency };
    if (period === "monthly") params.month = month;
    return params;
  }, [period, month, currency]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const entryParams = { ...filterParams, team_id: teamId };
      if (memberFilter !== "all") entryParams.member_id = memberFilter;
      const [dashboardResponse, membersResponse, entriesResponse, memberSummaryResponse] = await Promise.all([
        api.get("/team-credit/dashboard", { params: filterParams }),
        api.get("/team-credit/members", { params: { team_id: teamId } }),
        api.get("/team-credit/entries", { params: entryParams }),
        api.get("/team-credit/member-summary", { params: { ...filterParams, team_id: teamId } }),
      ]);
      setTeam((dashboardResponse.data.teams || []).find((item) => item.id === teamId) || null);
      setMembers(membersResponse.data || []);
      setEntries(entriesResponse.data.items || []);
      setSummary(entriesResponse.data.summary || {});
      setMemberRows(memberSummaryResponse.data.members || []);
    } catch (error) {
      toast.error(apiErrorText(error));
      setEntries([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  }, [filterParams, memberFilter, teamId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saveMember = async () => {
    if (!memberForm.name.trim()) return toast.error("Member name is required");
    setSavingMember(true);
    try {
      const payload = {
        name: memberForm.name.trim(),
        contact: memberForm.contact.trim() || null,
        notes: memberForm.notes.trim() || null,
      };
      if (editingMember?.id) {
        await api.put(`/team-credit/members/${editingMember.id}`, payload);
      } else {
        await api.post("/team-credit/members", { ...payload, team_id: teamId });
      }
      toast.success(editingMember?.id ? "Member updated" : "Member added");
      setMemberOpen(false);
      setMemberForm(EMPTY_MEMBER);
      setEditingMember(null);
      await loadData();
    } catch (error) {
      toast.error(apiErrorText(error));
    } finally {
      setSavingMember(false);
    }
  };

  const openMemberDialog = (member = null) => {
    setEditingMember(member);
    setMemberForm(member ? {
      name: member.name || "",
      contact: member.contact || "",
      notes: member.notes || "",
    } : EMPTY_MEMBER);
    setMemberOpen(true);
  };

  const deleteMember = async () => {
    if (!deletingMember?.id) return;
    setDeletingMemberBusy(true);
    try {
      await api.delete(`/team-credit/members/${deletingMember.id}`, { params: { cascade: true } });
      if (memberFilter === deletingMember.id) setMemberFilter("all");
      toast.success("Member and linked entries deleted");
      setDeletingMember(null);
      await loadData();
    } catch (error) {
      toast.error(apiErrorText(error));
    } finally {
      setDeletingMemberBusy(false);
    }
  };

  const openNewEntry = (memberId = "") => {
    if (!members.length) {
      toast.message("Add a member first");
      openMemberDialog();
      return;
    }
    setEditingEntry(null);
    if (memberId) setMemberFilter(memberId);
    setEntryOpen(true);
  };

  const deleteEntry = async () => {
    if (!deletingEntry?.id) return;
    setDeleting(true);
    try {
      await api.delete(`/team-credit/entries/${deletingEntry.id}`);
      toast.success("Entry deleted");
      setDeletingEntry(null);
      await loadData();
    } catch (error) {
      toast.error(apiErrorText(error));
    } finally {
      setDeleting(false);
    }
  };

  const filterLabel = period === "all" ? "All Time" : monthLabel(month);
  const selectedMember = members.find((member) => member.id === memberFilter);
  const scopeLabel = selectedMember ? selectedMember.name : "All members";
  const selectCurrency = (value) => {
    setCurrency(value);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("currency", value);
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <Layout
      title={team?.name || (loading ? "Team Credit" : "Team not found")}
      subtitle={team ? `${scopeLabel} · ${currency} credit and debit statement · Nepal time` : "Teams Credit"}
      testId="team-credit-details-page"
      actions={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate(`/team-credit?currency=${currency}`)} className="h-9">
            <ArrowLeft size={14} className="mr-1.5" /> Teams
          </Button>
          <Button variant="outline" onClick={() => openMemberDialog()} disabled={!team} className="h-9">
            <UserPlus size={14} className="mr-1.5" /> Add member
          </Button>
          <Button onClick={() => openNewEntry()} disabled={!team} className="h-9 bg-zinc-900 text-white hover:bg-zinc-800">
            <Plus size={14} className="mr-1.5" /> Add entry
          </Button>
        </div>
      }
    >
      <div className="stat-card mb-5 flex flex-wrap items-center gap-2 p-3">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="h-8 w-[140px]" data-testid="team-credit-ledger-period"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Time</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
        </Select>
        {period === "monthly" && (
          <>
            <button onClick={() => setMonth(monthAdd(month, -1))} className="flex h-8 w-8 items-center justify-center rounded-md border hairline bg-white hover:bg-zinc-50" aria-label="Previous month"><ChevronLeft size={14} /></button>
            <div className="min-w-[125px] px-2 text-center font-display text-[14px] font-semibold">{monthLabel(month)}</div>
            <button onClick={() => setMonth(monthAdd(month, 1))} className="flex h-8 w-8 items-center justify-center rounded-md border hairline bg-white hover:bg-zinc-50" aria-label="Next month"><ChevronRight size={14} /></button>
            {month !== currentNepalMonth() && <button onClick={() => setMonth(currentNepalMonth())} className="h-8 rounded-md border hairline bg-white px-3 text-[12px] hover:bg-zinc-50">This month</button>}
          </>
        )}
        <Select value={memberFilter} onValueChange={setMemberFilter}>
          <SelectTrigger className="h-8 w-[190px]" data-testid="team-credit-member-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All members</SelectItem>
            {members.map((member) => <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={currency} onValueChange={selectCurrency}>
          <SelectTrigger className="h-8 w-[120px]" data-testid="team-credit-ledger-currency"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TEAM_CREDIT_CURRENCIES.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-1.5 text-[11.5px] text-zinc-500"><CalendarDays size={13} /> Asia/Kathmandu</div>
      </div>

      {team?.notes && <div className="stat-card mb-5 px-4 py-3 text-[12.5px] text-zinc-600"><span className="mr-2 font-medium text-zinc-900">Team note:</span>{team.notes}</div>}

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <TotalCard label="Credit" value={summary.total_credit} currency={currency} icon={ArrowDownLeft} tone="emerald" footer={`${scopeLabel} · ${filterLabel}`} />
        <TotalCard label="Debit" value={summary.total_debit} currency={currency} icon={ArrowUpRight} tone="rose" footer={`${summary.entry_count || 0} entries · ${filterLabel}`} />
        <TotalCard label="Net Credit" value={summary.net_credit} currency={currency} icon={Scale} tone="sky" footer={`Credit − Debit · ${filterLabel}`} />
      </div>

      <section className="stat-card mb-5 overflow-hidden" data-testid="team-credit-members-table">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b hairline px-5 py-4">
          <div>
            <h2 className="font-display text-[16px] font-semibold text-zinc-900">Members</h2>
            <p className="mt-0.5 text-[12px] text-zinc-500">{currency} member balances for {filterLabel.toLowerCase()}</p>
          </div>
          <div className="flex items-center gap-1.5 text-[12px] text-zinc-500"><UsersRound size={14} /> {members.length} members</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[12.5px]">
            <thead>
              <tr className="border-b hairline bg-zinc-50/50 text-left text-[10.5px] uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3 font-medium">Member</th>
                <th className="px-4 py-3 text-right font-medium">Credit</th>
                <th className="px-4 py-3 text-right font-medium">Debit</th>
                <th className="px-4 py-3 text-right font-medium">Net</th>
                <th className="px-4 py-3 text-right font-medium">Entries</th>
                <th className="px-4 py-3 text-right font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {!loading && memberRows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No members yet. Add the first member to start the ledger.</td></tr>
              ) : memberRows.map((member) => (
                <tr key={member.id} className={memberFilter === member.id ? "bg-sky-50/50" : "row-hover"}>
                  <td className="px-4 py-3">
                    <button onClick={() => setMemberFilter(member.id)} className="text-left">
                      <div className="font-medium text-zinc-900 hover:underline">{member.name}</div>
                      <div className="text-[11px] text-zinc-500">{member.contact || "No contact"}</div>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-700">{fmtTeamCredit(member.total_credit, currency)}</td>
                  <td className="px-4 py-3 text-right font-mono text-rose-700">{fmtTeamCredit(member.total_debit, currency)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${Number(member.net_credit) < 0 ? "text-rose-700" : "text-zinc-900"}`}>{fmtTeamCredit(member.net_credit, currency)}</td>
                  <td className="px-4 py-3 text-right font-mono">{member.entry_count || 0}</td>
                  <td className="px-4 py-3 text-right"><div className="flex items-center justify-end gap-1">
                    <button onClick={() => openNewEntry(member.id)} className="rounded p-1.5 text-zinc-600 hover:bg-zinc-100" title={`Add entry for ${member.name}`} aria-label={`Add entry for ${member.name}`}><Plus size={14} /></button>
                    <button onClick={() => openMemberDialog(member)} className="rounded p-1.5 text-zinc-600 hover:bg-zinc-100" title={`Edit ${member.name}`} aria-label={`Edit ${member.name}`}><Pencil size={14} /></button>
                    <button onClick={() => setDeletingMember(member)} className="rounded p-1.5 text-rose-600 hover:bg-rose-50" title={`Delete ${member.name}`} aria-label={`Delete ${member.name}`}><Trash2 size={14} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="stat-card overflow-hidden" data-testid="team-credit-ledger-table">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b hairline px-5 py-4">
          <div>
            <h2 className="font-display text-[16px] font-semibold text-zinc-900">Ledger entries</h2>
            <p className="mt-0.5 text-[12px] text-zinc-500">{scopeLabel} · {filterLabel} · {currency}</p>
          </div>
          <div className="font-mono text-[12px] text-zinc-500">{entries.length} entries</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[12.5px]">
            <thead>
              <tr className="border-b hairline bg-zinc-50/50 text-left text-[10.5px] uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Member</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 text-right font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Reference</th>
                <th className="px-4 py-3 font-medium">Notes</th>
                <th className="px-4 py-3 font-medium">Added by</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500">Loading ledger...</td></tr>
              ) : !team ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-zinc-500">Team not found.</td></tr>
              ) : entries.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-zinc-500">No entries for {scopeLabel.toLowerCase()} in {filterLabel.toLowerCase()}.</td></tr>
              ) : entries.map((entry) => (
                <tr key={entry.id} className="row-hover">
                  <td className="whitespace-nowrap px-4 py-3 font-mono">{entry.date}</td>
                  <td className="whitespace-nowrap px-4 py-3 font-medium">{entry.member_name}</td>
                  <td className="px-4 py-3"><span className={`rounded border px-1.5 py-0.5 text-[10.5px] ${entry.direction === "credit" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}>{entry.direction === "credit" ? "Credit" : "Debit"}</span></td>
                  <td className={`whitespace-nowrap px-4 py-3 text-right font-mono ${entry.direction === "credit" ? "text-emerald-700" : "text-rose-700"}`}>{fmtTeamCredit(entry.amount ?? entry.amount_usd, entry.currency || "USD")}</td>
                  <td className="max-w-[180px] px-4 py-3">{entry.reference || "-"}</td>
                  <td className="max-w-[260px] whitespace-pre-wrap px-4 py-3 text-zinc-600">{entry.notes || "-"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-zinc-500">{entry.created_by_name || "-"}</td>
                  <td className="px-4 py-3"><div className="flex items-center justify-end gap-1">
                    <button onClick={() => { setEditingEntry(entry); setEntryOpen(true); }} className="rounded p-1.5 text-zinc-600 hover:bg-zinc-100" title="Edit entry" aria-label="Edit entry"><Pencil size={14} /></button>
                    <button onClick={() => setDeletingEntry(entry)} className="rounded p-1.5 text-rose-600 hover:bg-rose-50" title="Delete entry" aria-label="Delete entry"><Trash2 size={14} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={memberOpen} onOpenChange={(open) => { setMemberOpen(open); if (!open) setEditingMember(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{editingMember ? "Edit team member" : "Add team member"}</DialogTitle>
            <DialogDescription>{editingMember ? "Update this member without changing their ledger history." : `Add a person to ${team?.name || "this team"} before recording credit or debit.`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Member name *</Label><Input value={memberForm.name} onChange={(event) => setMemberForm({ ...memberForm, name: event.target.value })} placeholder="Full name" autoFocus /></div>
            <div className="space-y-1.5"><Label>Contact</Label><Input value={memberForm.contact} onChange={(event) => setMemberForm({ ...memberForm, contact: event.target.value })} placeholder="Phone, email, or handle" /></div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={memberForm.notes} onChange={(event) => setMemberForm({ ...memberForm, notes: event.target.value })} placeholder="Optional note" rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberOpen(false)}>Cancel</Button>
            <Button onClick={saveMember} disabled={savingMember} className="bg-zinc-900 text-white hover:bg-zinc-800">{savingMember ? "Saving..." : editingMember ? "Update member" : "Add member"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TeamCreditEntryDialog
        open={entryOpen}
        onOpenChange={(open) => { setEntryOpen(open); if (!open) setEditingEntry(null); }}
        team={team}
        members={members}
        initialMemberId={memberFilter === "all" ? "" : memberFilter}
        initialCurrency={currency}
        entry={editingEntry}
        onSaved={loadData}
      />

      <AlertDialog open={Boolean(deletingEntry)} onOpenChange={(open) => !open && !deleting && setDeletingEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this credit entry?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the {deletingEntry?.direction || "selected"} entry of {fmtTeamCredit(deletingEntry?.amount ?? deletingEntry?.amount_usd, deletingEntry?.currency || "USD")} for {deletingEntry?.member_name || "this member"} and recalculates totals. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteEntry} disabled={deleting} className="bg-rose-600 text-white hover:bg-rose-700">{deleting ? "Deleting..." : "Delete entry"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(deletingMember)} onOpenChange={(open) => !open && !deletingMemberBusy && setDeletingMember(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deletingMember?.name || "this member"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the member and {deletingMember?.all_entry_count || 0} linked ledger entries across all currencies and dates. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingMemberBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteMember} disabled={deletingMemberBusy} className="bg-rose-600 text-white hover:bg-rose-700">{deletingMemberBusy ? "Deleting..." : "Delete member and entries"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function TotalCard({ label, value, currency, icon: Icon, tone, footer }) {
  const tones = {
    emerald: { icon: "bg-emerald-50 text-emerald-600", value: "text-emerald-700" },
    rose: { icon: "bg-rose-50 text-rose-600", value: "text-rose-700" },
    sky: { icon: "bg-sky-50 text-sky-600", value: Number(value) < 0 ? "text-rose-700" : "text-sky-950" },
  };
  const selected = tones[tone] || tones.sky;
  return <div className="stat-card p-5"><div className="flex items-center justify-between"><span className="text-[10.5px] uppercase tracking-widest text-zinc-500">{label}</span><span className={`flex h-8 w-8 items-center justify-center rounded-md ${selected.icon}`}><Icon size={16} strokeWidth={1.7} /></span></div><div className={`mt-3 font-mono text-[23px] font-medium ${selected.value}`}>{fmtTeamCredit(value, currency)}</div><div className="mt-1.5 text-[10.5px] text-zinc-400">{footer}</div></div>;
}
